import i18n from '../../i18n';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * TRADUCTION DES LIBELLÉS DE TICKET — adaptation à la convention i18n de la BORNE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Côté console, les formateurs appellent `i18n.t(clé, { ns: 'tickets' })` : il existe
 * un namespace dédié `tickets`. La borne, elle, n'a QU'UN namespace (`translation`) et
 * ses clés SONT le texte français (`t('Sur place')`, `t('Merci de votre visite')`).
 *
 * On garde donc la même API (`tk` / `tkOrderTypeUpper` / `tkPayment`) pour ne pas
 * toucher aux modules portés, mais on la branche sur la convention de la borne :
 * chaque clé technique est traduite en son texte FRANÇAIS, qui sert ensuite de clé
 * i18next. Conséquences voulues :
 *   • si `locales/<lang>.json` contient l'entrée → texte traduit ;
 *   • sinon i18next renvoie la clé, c'est-à-dire le français : jamais de clé technique
 *     imprimée sur le papier.
 *
 * ⚠️ Interpolation faite ICI, pas par i18next : quand une clé est absente, i18next
 * renvoie la clé telle quelle et on ne peut pas garantir que `{{rate}}` sera remplacé.
 * On substitue donc `{rate}` (et `{{rate}}`) nous-mêmes, après traduction.
 *
 * ⚠️ Longueur : les tickets sont à chasse fixe (32/42/48 colonnes) et le remplissage
 * est calculé sur `label.length`. Les libellés doivent rester COURTS.
 */

/** Texte FRANÇAIS de chaque libellé de ticket — sert aussi de clé i18next. */
const FR_LABELS: Record<string, string> = {
  'header.defaultRestaurant': 'Restaurant',
  'header.phoneShort': 'Tel.',
  'header.orderUpper': 'COMMANDE',
  'header.receiptUpper': 'TICKET',
  'header.tableUpper': 'TABLE',
  'header.table': 'Table',
  'header.seller': 'Vendeur',
  'header.client': 'Client',
  'header.address': 'Adresse',
  'header.clientNumber': 'N° client',

  'items.sectionTitle': 'ARTICLES',
  'items.amountUpper': 'MONTANT',
  'items.note': 'Note',
  'items.none': 'Aucun article',
  'items.product': 'Produit',
  'items.article': 'article',
  'items.articles': 'articles',
  'items.customerNoteUpper': 'NOTE CLIENT',
  'items.notesUpper': 'NOTES',

  'totals.subtotalHT': 'Sous-total HT',
  'totals.tax': 'TVA',
  'totals.taxWithRate': 'TVA ({rate}%)',
  'totals.deliveryFeeShort': 'Livraison',
  'totals.discount': 'Remise',
  'totals.loyaltyDiscount': 'Remise fidelite',
  'totals.loyaltyDiscountPoints': 'Remise fidelite ({points} pts)',
  'totals.totalTTCUpper': 'TOTAL TTC',

  'payment.label': 'Paiement',
  'payment.mixed': 'Paiement mixte',
  'payment.toCollect': 'A ENCAISSER',
  'payment.notSpecified': 'Non specifie',
  'payment.cash': 'Especes',
  'payment.card': 'Carte',
  'payment.online': 'En ligne',

  'orderType.takeaway': 'A emporter',
  'orderType.dineIn': 'Sur place',
  'orderType.delivery': 'Livraison',
  'orderType.takeawayUpper': 'A EMPORTER',
  'orderType.dineInUpper': 'SUR PLACE',
  'orderType.deliveryUpper': 'LIVRAISON',

  'notes.delivery': 'Note livraison',
  'notes.pickup': 'Note retrait',

  'footer.thankYou': 'Merci de votre visite',
  'footer.thankYouExcl': 'Merci de votre visite !',
};

/** Substitution des marques `{nom}` / `{{nom}}` — voir l'avertissement ci-dessus. */
const interpolate = (text: string, params?: Record<string, unknown>): string => {
  if (!params) return text;
  let out = text;
  for (const [key, value] of Object.entries(params)) {
    out = out.split(`{{${key}}}`).join(String(value));
    out = out.split(`{${key}}`).join(String(value));
  }
  return out;
};

export const tk = (key: string, params?: Record<string, unknown>): string => {
  const fr = FR_LABELS[key] ?? key;
  let translated = fr;
  try {
    translated = i18n.t(fr, { defaultValue: fr }) as string;
  } catch {
    // i18next non initialisé (script Node de vérification) : on garde le français.
    translated = fr;
  }
  return interpolate(translated || fr, params);
};

/**
 * Libellé du TYPE de commande, en MAJUSCULES.
 * ⚠️ La borne n'émet que `takeaway` / `dine-in`, mais on couvre `delivery` : plusieurs
 * formateurs historiques utilisaient un ternaire BINAIRE, ce qui ferait sortir une
 * livraison en « SUR PLACE » le jour où le cas apparaîtra.
 */
export const tkOrderTypeUpper = (orderType?: string): string => {
  if (orderType === 'delivery') return tk('orderType.deliveryUpper');
  if (orderType === 'takeaway') return tk('orderType.takeawayUpper');
  return tk('orderType.dineInUpper');
};

/** Libellé d'un mode de paiement BRUT (`cash` → « Especes »). */
export const tkPayment = (method: string): string => {
  const key = `payment.${String(method || '').toLowerCase()}`;
  if (FR_LABELS[key]) return tk(key);
  const raw = String(method || '');
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};
